import { useEffect, useMemo, useRef, useState } from "react";
import { listReferences, triggerVoicePreview } from "../lib/api";
import { useDataCache } from "../contexts/DataCacheContext";
import type { Reference } from "../lib/types";
import { Icon } from "./icons";
import { VoicePreviewPlayer, GeneratePreviewButton } from "./VoicePreviewPlayer";

interface VoiceSelectorProps {
  selected: Reference | null;
  onSelect: (ref: Reference) => void;
}

type FilterKey = "all" | "provided" | "mine";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function VoiceSelector({ selected, onSelect }: VoiceSelectorProps) {
  const cache = useDataCache();
  const [references, setReferences] = useState<Reference[]>(cache.references);
  const [providedRefs, setProvidedRefs] = useState<Reference[]>(cache.providedRefs);
  const [loading, setLoading] = useState(cache.refsLoading && cache.references.length === 0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [playingName, setPlayingName] = useState<string | null>(null);
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null);
  const [previewProgress, setPreviewProgress] = useState<Record<string, number>>({});
  const [retryingPreview, setRetryingPreview] = useState<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement>(null);
  const previewAudios = useRef<Map<string, HTMLAudioElement | null>>(new Map());

  useEffect(() => {
    if (cache.references.length > 0 || cache.providedRefs.length > 0) {
      setReferences(cache.references);
      setProvidedRefs(cache.providedRefs);
      setLoading(false);
    }
    void loadReferences();
  }, []);

  async function loadReferences() {
    try {
      const result = await listReferences();
      setReferences(result.items);
      setProvidedRefs(result.provided ?? []);
    } catch (err) {
      console.error("Failed to load references:", err);
    } finally {
      setLoading(false);
    }
  }

  const allRefs = useMemo(() => {
    return [
      ...providedRefs.map((r) => ({ ...r, _isProvided: true })),
      ...references.map((r) => ({ ...r, _isProvided: r.provided ?? false })),
    ];
  }, [providedRefs, references]);

  const filtered = useMemo(() => {
    return allRefs.filter((v) => {
      if (filter === "provided" && !v._isProvided) return false;
      if (filter === "mine" && v._isProvided) return false;
      if (search && !v.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [allRefs, filter, search]);

  const handlePreview = (ref: Reference, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audioRef.current || !ref.previewUrl) return;
    // Stop the voice-preview if it's currently playing — only one audio at a time.
    if (previewPlaying) {
      previewAudios.current.get(previewPlaying)?.pause();
      setPreviewPlaying(null);
    }
    if (playingName === ref.name) {
      audioRef.current.pause();
      setPlayingName(null);
    } else {
      audioRef.current.src = ref.previewUrl;
      void audioRef.current.play();
      setPlayingName(ref.name);
    }
  };

  const handleVoicePreviewPlay = (ref: Reference) => {
    const target = previewAudios.current.get(ref.name);
    if (!target || !ref.voicePreviewUrl) return;
    // Pause the source player if it's playing — one audio at a time.
    if (audioRef.current && playingName) {
      audioRef.current.pause();
      setPlayingName(null);
    }
    if (previewPlaying === ref.name) {
      target.pause();
      setPreviewPlaying(null);
      return;
    }
    if (previewPlaying) {
      previewAudios.current.get(previewPlaying)?.pause();
    }
    void target.play();
    setPreviewPlaying(ref.name);
  };

  const handleVoicePreviewSeek = (ref: Reference, fraction: number) => {
    const target = previewAudios.current.get(ref.name);
    if (!target) return;
    const clamped = Math.max(0, Math.min(1, fraction));
    if (target.duration && Number.isFinite(target.duration)) {
      target.currentTime = clamped * target.duration;
    }
    setPreviewProgress((prev) => ({ ...prev, [ref.name]: clamped }));
    if (previewPlaying !== ref.name) {
      if (previewPlaying) {
        previewAudios.current.get(previewPlaying)?.pause();
      }
      if (audioRef.current && playingName) {
        audioRef.current.pause();
        setPlayingName(null);
      }
      void target.play();
      setPreviewPlaying(ref.name);
    }
  };

  const handleGeneratePreview = async (ref: Reference) => {
    const key = `${ref.name}::${ref.s3Key}`;
    if (retryingPreview.has(key)) return;
    setRetryingPreview((s) => new Set(s).add(key));
    try {
      await triggerVoicePreview(ref.name);
      void loadReferences();
    } catch (err) {
      console.error("Failed to trigger voice preview:", err);
    } finally {
      setRetryingPreview((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="voice-grid">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="skeleton-card" style={{ height: 130 }} />
        ))}
      </div>
    );
  }

  if (allRefs.length === 0) {
    return (
      <div className="empty-state">
        <div className="ico"><Icon.Library /></div>
        <h3>No voice references yet</h3>
        <p>Upload a reference voice in the Library tab first.</p>
      </div>
    );
  }

  return (
    <div>
      <audio ref={audioRef} onEnded={() => setPlayingName(null)} preload="none" />

      <div className="voice-tools">
        <div className="voice-search">
          <Icon.Search />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search voices…"
          />
        </div>
        <div className="voice-filter">
          {(
            [
              ["all", "All"],
              ["provided", "Curated"],
              ["mine", "Yours"],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              type="button"
              className={filter === k ? "on" : ""}
              onClick={() => setFilter(k)}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="voice-grid">
        {filtered.map((ref, idx) => {
          const sel =
            selected?.name === ref.name && selected?.s3Key === ref.s3Key;
          const tag = ref._isProvided ? "provided" : "custom";
          const status = ref.voicePreviewStatus;
          const isCrossUser = !!ref.provided && !!ref.ownerUserId;
          const previewReady = status === "complete" && !!ref.voicePreviewUrl;
          const retryKey = `${ref.name}::${ref.s3Key}`;
          const retrying = retryingPreview.has(retryKey);
          const generateStatus: "idle" | "queued" | "processing" | "failed" =
            status === "queued" || status === "processing"
              ? status
              : status === "failed"
                ? "failed"
                : "idle";
          return (
            <div
              key={`${ref.name}-${ref.s3Key}`}
              className={`voice-card${sel ? " sel" : ""}`}
              onClick={() => onSelect(ref)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(ref);
                }
              }}
            >
              <div className="vc-hero">
                {ref.imageUrl ? (
                  <img src={ref.imageUrl} alt="" crossOrigin="anonymous" />
                ) : (
                  <span className="vc-hero-initials">{initials(ref.name)}</span>
                )}
                <div className="vc-check">
                  <Icon.Check />
                </div>
              </div>
              <div className="vc-body">
                <div className="vc-row">
                  <div className="vc-name">
                    <div className="n">{ref.name}</div>
                    <div className="m">
                      {Math.round(ref.duration)}s
                      {ref.uploadedAt
                        ? ` · ${new Date(ref.uploadedAt).toLocaleDateString()}`
                        : ""}
                    </div>
                  </div>
                  <span className={`vc-tag${ref._isProvided ? " prov" : ""}`}>{tag}</span>
                  <button
                    type="button"
                    className="vc-play"
                    onClick={(e) => handlePreview(ref, e)}
                    aria-label={playingName === ref.name ? "Pause source" : "Play source"}
                    title="Play source recording"
                  >
                    {playingName === ref.name ? <Icon.Pause /> : <Icon.Play />}
                  </button>
                </div>
                {previewReady ? (
                  <VoicePreviewPlayer
                    audioUrl={ref.voicePreviewUrl}
                    peaks={ref.voicePreviewPeaks}
                    backfillTarget={
                      ref.provided
                        ? undefined
                        : { kind: "reference-voice-preview", refName: ref.name }
                    }
                    playing={previewPlaying === ref.name}
                    progress={previewProgress[ref.name] ?? 0}
                    seed={idx}
                    onPlayPause={(e) => {
                      e.stopPropagation();
                      handleVoicePreviewPlay(ref);
                    }}
                    onSeek={(fraction, e) => {
                      e.stopPropagation();
                      handleVoicePreviewSeek(ref, fraction);
                    }}
                    onTimeUpdate={(el) => {
                      if (el.duration && Number.isFinite(el.duration)) {
                        setPreviewProgress((prev) => ({
                          ...prev,
                          [ref.name]: Math.min(1, el.currentTime / el.duration),
                        }));
                      }
                    }}
                    onLoadedMetadata={() => {}}
                    onEnded={() => {
                      setPreviewPlaying((p) => (p === ref.name ? null : p));
                      setPreviewProgress((prev) => ({ ...prev, [ref.name]: 0 }));
                    }}
                    audioRef={(el) => {
                      previewAudios.current.set(ref.name, el);
                    }}
                  />
                ) : (
                  <GeneratePreviewButton
                    status={generateStatus}
                    retrying={retrying}
                    disabled={isCrossUser}
                    errorTitle={ref.voicePreviewError}
                    onClick={() => handleGeneratePreview(ref)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

